/**
 * Push Notification Permission Prompt Hook
 *
 * Checks push notification permission status on native iOS and shows
 * a dismissable prompt encouraging users to enable notifications.
 * Once dismissed, the prompt is not shown again (user can re-enable
 * via Settings > Notifications). Re-checks on foreground to silently
 * clear the prompt if the user enabled notifications in device settings.
 */

import { useState, useEffect, useCallback } from 'react'
import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import { App } from '@capacitor/app'
import { Browser } from '@capacitor/browser'
import { isNative } from '@/lib/capacitor'

const DISMISSED_KEY = 'zrm_push_prompt_dismissed'

interface UsePushNotificationEnforcementReturn {
  showModal: boolean
  openSettings: () => void
  dismiss: () => void
}

function isDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === 'true'
  } catch {
    return false
  }
}

function persistDismissal(): void {
  try {
    localStorage.setItem(DISMISSED_KEY, 'true')
  } catch {
    // localStorage unavailable — dismiss for this session only
  }
}

export function usePushNotificationEnforcement(): UsePushNotificationEnforcementReturn {
  const [showModal, setShowModal] = useState(false)

  const checkPermission = useCallback(async () => {
    if (!isNative()) return
    if (!Capacitor.isPluginAvailable('PushNotifications')) return
    if (isDismissed()) {
      setShowModal(false)
      return
    }

    try {
      const result = await PushNotifications.checkPermissions()
      if (result.receive === 'denied') {
        setShowModal(true)
      } else if (result.receive === 'prompt') {
        const permResult = await PushNotifications.requestPermissions()
        setShowModal(permResult.receive === 'denied')
      } else {
        setShowModal(false)
      }
    } catch (err) {
      console.error('Permission check error:', err)
    }
  }, [])

  useEffect(() => {
    checkPermission()
    const listenerPromise = App.addListener('appStateChange', (state) => {
      if (state.isActive) checkPermission()
    })
    return () => {
      listenerPromise.then((h) => h.remove())
    }
  }, [checkPermission])

  const openSettings = useCallback(async () => {
    try {
      await Browser.open({ url: 'app-settings:' })
    } catch {
      console.warn('Could not open settings automatically')
    }
  }, [])

  const dismiss = useCallback(() => {
    persistDismissal()
    setShowModal(false)
  }, [])

  return { showModal, openSettings, dismiss }
}
